import React from "react";
import jsPDF from "jspdf";
// eslint-disable-next-line
import lodashGet from "lodash/get";
import { format } from "date-fns";
import canvg from "canvg";
import QRCode from "react-qr-code";
import JsBarcode from "jsbarcode";
import ReactDOMServer from "react-dom/server";

import ImageEye from "@material-ui/icons/RemoveRedEye";
import ContentCreate from "@material-ui/icons/Create";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import SyncAlert from "mdi-material-ui/SyncAlert";

import { toFixed, toFixedSigned, getDistinctValues, isEmpty, listFromCsv, isTrue, toFixedValue } from "../../utils/helpers";
import { findSizeBreakupList, getCutOrderFlowInfoList } from "../../helpers/orderHelper";
import { getRollNo } from "../../helpers/warehouseHelper";
import { getGroupName } from "../grouping/groupingHelper";
import { setDefault, minReqHeight, totalPagesExp, addTable } from "../../helpers/pdfHelper";
import { REMNANT_LAY, BundleSheetType, MULTI_PART_MANUAL_PLAN, DEFAULT_SEPARATOR } from "../../constants";
import { MarkerType, GroupType, ReserveType, SpreadingType, LayType, OState } from "../../utils/types";

import { RestMethods, FETCH_START, FETCH_END } from "jazasoft";

import { dataProvider } from "../../App";

export const DataType = {
  LAY_SLIP: "LaySlip",
  LAY: "Lay",
  MISSING_ROLL: "MissingRoll",
  MARKER: "Marker",
  LIVE_REPORT: "LiveReport",
  PART_RECUT_PART: "PartReCutPart",
  PART_RECUT_ROLL: "PartReCutRoll",
  END_BITS: "EndBits",
  ISSUED_ROLL: "IssuedRoll",
  GARMENT_RECUT_SUMMARY: "GarmentReCutSummary",
  GARMENT_RECUT_CUT_PLAN: "GarmentReCutCutPlan",
  GARMENT_RECUT_ROLL: "GarmentReCutRoll"
};

const barcodeOptions = { fontSize: 30 };

const getListFromCSV = values => {
  if (!values) return [];
  return values
    .split(",")
    .filter(e => e.trim())
    .map(e => e.trim());
};

export const getSmallestML = (order, orderFlow, styleColor, markerList) => {
  if (!order || !orderFlow || !styleColor) return 1.0; // TODO: Review hard coded value
  const flowInfo = orderFlow?.flowInfoList?.find(fi => fi.styleColor === styleColor);
  const part = flowInfo?.partList?.find(p => p.partName === orderFlow.part);
  let smallestML = flowInfo ? part.bomCu : 1.0; // TODO: Review hard coded value
  let markers = markerList;
  if (isEmpty(markerList)) {
    markers = orderFlow.markerList && orderFlow.markerList.filter(m => m.completed && m.markerType === MarkerType.REMNANT && m.length > 0);
  }
  if (markers && markers.length > 0) {
    smallestML = markers.sort((a, b) => a.layLength - b.layLength)[0].layLength;
  }
  return smallestML;
};

export const isFeedbackCompleted = orderFlow => {
  if (isEmpty(orderFlow)) return false;
  const remMarkers = orderFlow.markerList && orderFlow.markerList.filter(m => m.markerType === MarkerType.REMNANT);

  const feedbackCompleted =
    orderFlow.stateValue >= OState.CLOSURE_CONFIRMATION_PENDING.stateValue ||
    orderFlow.stateValue === OState.REMNANT_FEEDBACK_COMPLETED.stateValue ||
    (orderFlow.stateValue === OState.MAIN_FEEDBACK_COMPLETED.stateValue && isEmpty(remMarkers)) ||
    (orderFlow.stateValue === OState.FEEDBACK_COMPLETED.stateValue && orderFlow.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN);

  return feedbackCompleted;
};

export const getHeader = ({ dataType, order, orderFlow, rollFields = [], bundleParts, editing, styleColor }) => {
  let columns = [];
  if (dataType === DataType.LAY_SLIP) {
    columns = [
      { dataKey: "marker", title: "Marker", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "groupName", title: "Group", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "layLength", title: "Lay Length", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "way", title: "Way", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      {
        dataKey: "markerPlies",
        title: "Planned Plies",
        rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0)
      },
      { dataKey: "layNo", title: "Lay No", align: "center" },
      { dataKey: "plies", title: "Planned Plies", align: "center" },
      { dataKey: "actualPlies", title: "Actual Plies", align: "center" },
      { dataKey: "rollCount", title: "No. Of Rolls", align: "center" },
      { dataKey: "status", title: "Status", align: "center" },
      { dataKey: "action", title: "Action", align: "center" }
    ];
    if (orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN) {
      columns = columns.filter(c => !["rollCount", "plies"].includes(c.dataKey));
    } else {
      columns = columns.filter(c => !["markerPlies"].includes(c.dataKey));
    }
  } else if (dataType === DataType.LAY) {
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNo", title: "F. Roll No." },
      { dataKey: "supplierRollNo", title: "S. Roll No." },
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "length", title: "Length" },
      { dataKey: "width", title: "Width" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "layLength", title: "Lay Length" },
      { dataKey: "plannedPly", title: "Planned Plies" },
      { dataKey: "plannedBalance", title: "Planned Balance" },
      { dataKey: "actualPly", title: "Actual Plies" },
      { dataKey: "actualBalance", title: "Actual Balance" },
      { dataKey: "shortageExcess", title: "Shortage/Excess" },
      { dataKey: "damaged", title: "Damaged/Rejected" },
      { dataKey: "widthVariation", title: "Width Variation" }
    ];
    if (!rollFields.includes("supplierRollNo")) {
      columns = columns.filter(c => c.dataKey !== "supplierRollNo");
    }
  } else if (dataType === DataType.MISSING_ROLL) {
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNo", title: "Roll No." },
      { dataKey: "color", title: "Color" },
      { dataKey: "length", title: "Length" },
      { dataKey: "width", title: "Width" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "found", title: "Found?" }
    ];
  } else if (dataType === DataType.MARKER) {
    columns = [
      { title: "Marker", dataKey: "marker" },
      { title: "Shrinkage", dataKey: "shrinkage" },
      { title: "Way", dataKey: "way" },
      { title: "Lay Length", dataKey: "layLength" },
      { title: "No. Of Plies", dataKey: "plies" }
    ];
  } else if (dataType === DataType.LIVE_REPORT && order && orderFlow && styleColor) {
    const sizeBreakupList = orderFlow?.flowInfoList?.filter(fi => fi.styleColor === styleColor).flatMap(fi => fi.fpoSizeBreakupList || []) || [];
    const sizeList = sizeBreakupList.sort((a, b) => a.serialNo - b.serialNo).map(sb => ({ dataKey: sb.size, title: sb.size }));
    columns = [{ dataKey: "label", title: "Label" }, ...sizeList, { dataKey: "total", title: "Total" }];
  } else if (dataType === DataType.PART_RECUT_PART && order && bundleParts) {
    columns = [
      { dataKey: "defectType", title: "Defect Type" },
      ...bundleParts.map(part => ({
        dataKey: part,
        title: part,
        element: ({ record, column }) => <span>{record[column.dataKey] === 0 ? "-" : record[column.dataKey]}</span>
      })),
      { dataKey: "total", title: "Total" }
    ];
  } else if (dataType === DataType.PART_RECUT_ROLL) {
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNo", title: "Roll Number" },
      { dataKey: "rollType", title: "Roll Type" },
      // { dataKey: "groupName", title: "Group" },
      { dataKey: "shrinkageValue", title: "Pattern" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "available", title: "Available" },
      { dataKey: "allowed", title: "Allowed" }
    ];
    if (editing) {
      columns.push({ dataKey: "recutting", title: "Used (Recutting)" });
    }
  } else if (dataType === DataType.END_BITS) {
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNo", title: "Roll Number" },
      { dataKey: "rollType", title: "Roll Type" },
      // { dataKey: "groupName", title: "Group" },
      { dataKey: "shrinkageValue", title: "Pattern" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "length", title: "Length" },
      { dataKey: "available", title: "End Bits" }
    ];
  } else if (dataType === DataType.ISSUED_ROLL) {
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNo", title: "Roll Number" },
      { dataKey: "rollType", title: "Roll Type" },
      // { dataKey: "groupName", title: "Group" },
      { dataKey: "shrinkageValue", title: "Pattern" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "length", title: "Length" },
      { dataKey: "usedInPart", title: "Used (Recutting)" }
    ];
  } else if (dataType === DataType.GARMENT_RECUT_SUMMARY && order && order.fpoSizeBreakupList && orderFlow && styleColor) {
    const sizeBreakupList = orderFlow?.flowInfoList?.filter(fi => fi.styleColor === styleColor).flatMap(fi => fi.fpoSizeBreakupList || []) || [];
    const sizeList = sizeBreakupList.sort((a, b) => a.serialNo - b.serialNo).map(sb => ({ dataKey: sb.size, title: sb.size }));

    columns = [{ dataKey: "label", title: "Label" }, ...sizeList, { dataKey: "total", title: "Total" }];
  } else if (dataType === DataType.GARMENT_RECUT_ROLL) {
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNo", title: "Roll Number" },
      { dataKey: "rollType", title: "Roll Type" },
      { dataKey: "groupName", title: "Group" },
      { dataKey: "color", title: "Style Color" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "length", title: "Length" },
      { dataKey: "width", title: "Width" },
      { dataKey: "reCutGarment", title: "Used (Recutting)" },
      { dataKey: "available", title: "Available" }
    ];
  } else if (dataType === DataType.GARMENT_RECUT_CUT_PLAN) {
    columns = [
      { dataKey: "name", title: "Marker" },
      { dataKey: "noOfWays", title: "Way" },
      { dataKey: "groupName", title: "Group" },
      { dataKey: "layLength", title: "Lay Length" },
      { dataKey: "efficiency", title: "Efficiency" },
      { dataKey: "layNo", title: "Lay No" },
      { dataKey: "status", title: "Status" },
      { dataKey: "action", title: "Action" }
    ];
  }
  return columns;
};

export const getData = ({
  dataType,
  layMap,
  layTab,
  lay,
  order,
  fabrics,
  orderFlow,
  bundleParts,
  styleColor,
  defectTypes,
  rollInfoList,
  onViewClick,
  onEditClick,
  onAddClick,
  onSyncClick,
  onDeleteClick
}) => {
  let rows = [];

  if (dataType === DataType.LAY_SLIP && layMap && layTab) {
    const isManualPlan = orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN;
    let layList = layMap[layTab]?.sort((a, b) => a.layNo - b.layNo) || [];
    if (isManualPlan) {
      layList =
        orderFlow?.markerList
          ?.sort((a, b) => a.markerNo - b.markerNo)
          ?.flatMap(marker => {
            const lays = layList.filter(l => l.marker?.id === marker?.id);
            if (isEmpty(lays)) {
              return [{ marker, layNo: null, rowList: [] }];
            }
            return lays;
          }) || [];
    }
    if (layList && layList.length > 0) {
      const completeRowList = layList.flatMap(e => e.rowList || []);
      let markerSet = new Set();
      let markerNo = 1;
      rows = layList.map((lay, idx) => {
        const { marker, layNo, rowList } = lay;
        const plies = rowList?.reduce((acc, e) => acc + e.plannedPly, 0);
        const fabricQty = lay?.rowList.reduce((acc, row) => acc + marker.layLength * row.plannedPly, 0);
        const markerPlies = marker?.markerPlyList?.reduce((acc, mp) => acc + mp.plannedPlies, 0);
        let serialNo;
        if (!markerSet.has(marker.id)) {
          serialNo = markerNo++;
          markerSet.add(marker.id);
        }

        const actualPlies = completeRowList.filter(e => e.completed && e.actualLayNo === layNo).reduce((acc, row) => acc + row.actualPly || 0, 0);

        let actionElements = [
          <ImageEye key="view" style={{ marginRight: 8, cursor: "pointer" }} onClick={onViewClick && onViewClick(lay)} />,
          <ContentCreate
            key="edit"
            style={{ cursor: "pointer" }}
            onClick={onEditClick && !isEmpty(layNo) && onEditClick({ layTab, lay, layList, orderFlow })}
          />
        ];
        if (isManualPlan) {
          actionElements.push(<AddIcon style={{ cursor: "pointer" }} key="add" onClick={onAddClick && onAddClick(marker)} />);
          actionElements.push(<DeleteIcon key="delete" style={{ cursor: "pointer" }} onClick={onDeleteClick && onDeleteClick(lay)} />);
        }
        if (lay.syncCompleted === false) {
          actionElements.push(<SyncAlert style={{ cursor: "pointer" }} key="sync-alert" onClick={onSyncClick && onSyncClick({ lay, orderFlow })} />);
        }

        return {
          serialNo,
          marker: marker.name,
          groupName: getGroupName(marker.group),
          layLength: marker.layLength,
          way: marker.noOfWays,
          layNo,
          layCount: layList.filter(lay => marker.id === lay.marker.id).length,
          plies,
          markerPlies,
          actualPlies,
          rollCount: rowList && rowList.length,
          fabricQty: toFixed(fabricQty, 1),
          status: lay.state,
          action: actionElements
        };
      });
    }
  } else if (dataType === DataType.LAY && lay) {
    rows = lay.rowList
      .sort((a, b) => a.serialNo - b.serialNo)
      .filter(row => isTrue(row.completed))
      .map(({ rollInfo, ...row }, idx) => ({
        ...row,
        rollNo: rollInfo.rollNo,
        supplierRollNo: rollInfo.supplierRollNo,
        color: rollInfo.color,
        shade: rollInfo.shade,
        width: rollInfo.width,
        length: `${toFixed(rollInfo.length, 1)}`,
        serialNo: idx + 1,
        layLength: lay.marker.layLength,
        shortageExcess: toFixed(row.shortageExcess, 1),
        damaged: toFixed(row.damagedLength, 1),
        widthVariation: isEmpty(row.widthVariation)
          ? ""
          : JSON.parse(row.widthVariation)
              .map(e => `${e.width} cm: ${e.length} mtr`)
              .join(",\n")
      }));
  } else if (dataType === DataType.LIVE_REPORT && order && orderFlow && !isEmpty(orderFlow?.markerList) && layMap && styleColor) {
    const sizeBreakupList = findSizeBreakupList(orderFlow, order);
    const sizeList = sizeBreakupList.map(sb => ({ dataKey: sb.sizeKey, title: sb.size }));

    const approvedOrderCutQtyMap = JSON.parse(orderFlow.orderConfig.approvedOrderCutQty || "{}");
    const layList = Object.keys(layMap).flatMap(key => layMap[key]);
    const rowList = layList.flatMap(lay => (lay.colorShade === LayType.RECUTTING ? [] : lay.rowList.map(row => ({ ...row, marker: lay.marker }))));

    // Order Qty
    const orderQty = sizeList.reduce((acc, { dataKey }) => {
      const qty = sizeBreakupList.filter(sb => sb.sizeKey === dataKey).reduce((acc, sb) => acc + sb.qty, 0);
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalOrderQty = Object.keys(orderQty).reduce((acc, key) => acc + orderQty[key], 0);
    rows.push({ ...orderQty, styleColor, total: totalOrderQty, label: "Order Qty" });

    // Approved Qty
    const approvedQty = approvedOrderCutQtyMap[styleColor] || {};
    let totalApprovedQty = Object.keys(approvedQty).reduce((acc, key) => acc + approvedQty[key], 0);
    rows.push({ ...approvedQty, styleColor, total: totalApprovedQty, label: "Approved Qty" });

    // Cut Qty
    const cutQty = sizeList.reduce((acc, { dataKey }) => {
      const qty = rowList
        .filter(row => row.styleColor === styleColor && row.completed)
        .reduce((acc, row) => {
          const sizeRatio = row.marker.sizeRatioList.find(sr => sr.sizeKey === dataKey);
          const plies = SpreadingType.FACE_ONE_WAY !== row.marker.spreadingType ? row.actualPly / 2 : row.actualPly;
          const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
          return acc + qty;
        }, 0);
      return { ...acc, [dataKey]: qty };
    }, {});

    let totalCutQty = Object.keys(cutQty).reduce((acc, key) => acc + cutQty[key], 0);
    rows.push({ ...cutQty, styleColor, total: totalCutQty, label: "Cut Qty" });

    if (orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN) {
      //Balance Qty
      const balanceQty = sizeList.reduce((acc, { dataKey }) => {
        const balance = (approvedQty[dataKey] || 0) - (cutQty[dataKey] || 0);
        return { ...acc, [dataKey]: toFixedSigned(-balance, 0) };
      }, {});

      let totalBalanceQty = Object.keys(balanceQty).reduce((acc, key) => acc + Number(balanceQty[key]), 0);
      rows.push({ ...balanceQty, styleColor, total: totalBalanceQty, label: "Shortage/Excess" });
    } else {
      //Balance Qty
      const balanceQty = sizeList.reduce((acc, { dataKey }) => {
        const qty = rowList
          .filter(row => row.styleColor === styleColor && !row.completed)
          .reduce((acc, row) => {
            const sizeRatio = row.marker?.sizeRatioList?.find(sr => sr.sizeKey === dataKey);
            const plies = SpreadingType.FACE_ONE_WAY !== row.marker.spreadingType ? row.actualPly / 2 : row.actualPly;
            const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
            return acc + qty;
          }, 0);
        return { ...acc, [dataKey]: qty };
      }, {});

      // For Multi-Stage case,Only Half Layslip is present. we need to add planned remnant qty in balance to cut qty
      if (!layList.find(lay => lay.layType === REMNANT_LAY)) {
        const markers = orderFlow.markerList.filter(m => m.markerType === MarkerType.REMNANT);
        sizeList.forEach(({ dataKey }) => {
          const qty = markers.reduce((acc, marker) => {
            const sizeRatio = marker.sizeRatioList.find(sr => sr.sizeKey === dataKey);
            const ply = marker.markerPlyList && marker.markerPlyList.find(mp => mp.styleColor === styleColor);
            const qty = sizeRatio && ply ? sizeRatio.ratio * ply.plannedPlies : 0;
            return acc + qty;
          }, 0);
          balanceQty[dataKey] = balanceQty[dataKey] + qty;
        });
      }
      let totalBalanceQty = Object.keys(balanceQty).reduce((acc, key) => acc + balanceQty[key], 0);
      rows.push({ ...balanceQty, styleColor, total: totalBalanceQty, label: "Balance to Cut" });

      // Shortage/Excess Qty
      const shortageExcessQty = sizeList.reduce((acc, { dataKey }) => {
        const qty = rowList
          .filter(row => row.completed && row.styleColor === styleColor)
          .reduce((acc, row) => {
            const sizeRatio = row.marker.sizeRatioList.find(sr => sr.sizeKey === dataKey);
            let plyDiff = row.actualPly - row.plannedPly;
            if (SpreadingType.FACE_ONE_WAY !== row.marker.spreadingType) {
              plyDiff = plyDiff / 2;
            }
            const qty = sizeRatio ? sizeRatio.ratio * plyDiff : 0;
            return acc + qty;
          }, 0);
        return { ...acc, [dataKey]: qty };
      }, {});

      // If REMANT_RETAINED markers are present,then Remnant Markers are ReGenerated
      // Add shortage/excess incurred after adjusting Remnant Cutplan from approved qty
      const remRetMarkers = orderFlow.markerList.filter(m => m.markerType === MarkerType.REMNANT_RETAINED);
      if (remRetMarkers.length > 0) {
        sizeList.forEach(({ dataKey }) => {
          const approved = orderFlow.markerList
            .filter(m => m.markerType === MarkerType.REMNANT_RETAINED)
            .reduce((acc, marker) => {
              const sizeRatio = marker.sizeRatioList.find(sr => sr.sizeKey === dataKey);
              const ply = marker.markerPlyList && marker.markerPlyList.find(mp => mp.styleColor === styleColor);
              const qty = sizeRatio && ply ? sizeRatio.ratio * ply.plannedPlies : 0;
              return acc + qty;
            }, 0);

          const reAdjusted = orderFlow.markerList
            .filter(m => m.markerType === MarkerType.REMNANT)
            .reduce((acc, marker) => {
              const sizeRatio = marker.sizeRatioList.find(sr => sr.sizeKey === dataKey);
              const ply = marker.markerPlyList && marker.markerPlyList.find(mp => mp.styleColor === styleColor);
              const qty = sizeRatio && ply ? sizeRatio.ratio * ply.plannedPlies : 0;
              return acc + qty;
            }, 0);
          shortageExcessQty[dataKey] = shortageExcessQty[dataKey] - (approved - reAdjusted);
        });
      }
      let totalshortageExcessQty = Object.keys(shortageExcessQty).reduce((acc, key) => acc + shortageExcessQty[key], 0);
      rows.push({ ...shortageExcessQty, styleColor, total: totalshortageExcessQty, label: "Shortage/Excess" });
    }
  } else if (dataType === DataType.PART_RECUT_PART && order && orderFlow && bundleParts && defectTypes) {
    const reCutPartList = orderFlow.reCutPartList && orderFlow.reCutPartList.filter(e => e.styleColor === styleColor);
    rows = defectTypes.map(({ key, value }) => {
      const partData = bundleParts.reduce((acc, part) => {
        const reCutPartInfo = reCutPartList && reCutPartList.find(e => e.reCutPart === part && e.type === key);
        const qty = reCutPartInfo ? reCutPartInfo.qty : 0;
        return { ...acc, [part]: qty };
      }, {});
      const total = Object.keys(partData).reduce((acc, key) => acc + partData[key], 0);
      return { defectType: value, defectKey: key, total, ...partData };
    });
    const totalRow = bundleParts.reduce((acc, part) => {
      const qty = rows.reduce((acc, row) => acc + row[part], 0);
      return { ...acc, [part]: qty };
    }, {});
    const overallTotal = Object.keys(totalRow).reduce((acc, key) => acc + totalRow[key], 0);
    rows.push({ ...totalRow, defectType: "Total", total: overallTotal });
  } else if (dataType === DataType.PART_RECUT_ROLL && order && orderFlow && fabrics && layMap && styleColor) {
    const rollList = rollInfoList || [];

    const layList = Object.keys(layMap).flatMap(key => layMap[key]);
    const rowList = layList.flatMap(lay =>
      lay.rowList.map(row => ({ ...row, layType: lay.layType, layNo: lay.layNo, layLength: lay.marker.layLength }))
    );

    if (rollList) {
      const groups = orderFlow.groupList && orderFlow.groupList.filter(g => g.type === GroupType.FINAL);
      rows = rollList
        .map(roll => {
          const rows = rowList.filter(row => row.rollNo === roll.rollNo && row.layType !== REMNANT_LAY).sort((a, b) => b.layNo - a.layNo);
          const lastMainRow = rows && rows[0];
          const group = (groups && groups.find(g => g.id === roll.groupId)) || {};
          let allowed;
          if (lastMainRow) {
            if (lastMainRow.completed) {
              allowed = roll.available;
            } else {
              allowed = parseFloat(lastMainRow.actualBalance);
            }
          } else {
            allowed = roll.available;
          }
          return {
            ...roll,
            rollNo: getRollNo(roll.rollNo),
            group,
            shrinkageValue: isEmpty(group?.shrinkageValue)
              ? ""
              : `${isEmpty(group?.patternNumber) ? "" : `${group.patternNumber} - `}L${toFixed(group.warpPattern)}%,W${toFixed(group.weftPattern)}%`,
            groupName: getGroupName(group),
            allowed: toFixed(allowed),
            available: toFixed(roll.available),
            usedInPart: toFixed(roll.usedInPart, 2)
          };
        })
        .sort((a, b) => (`${a.rollType}-${a.rollNo}` < `${b.rollType}-${b.rollNo}` ? -1 : 1));
    }
  } else if (dataType === DataType.GARMENT_RECUT_SUMMARY && order && !isEmpty(orderFlow?.orderConfig) && layMap && styleColor) {
    const sizeBreakupList = orderFlow?.flowInfoList?.filter(fi => fi.styleColor === styleColor).flatMap(fi => fi.fpoSizeBreakupList || []) || [];
    const sizeList = sizeBreakupList.sort((a, b) => a.serialNo - b.serialNo).map(sb => ({ dataKey: sb.size, title: sb.size }));

    const approvedOrderCutQtyMap = JSON.parse(orderFlow.orderConfig.approvedOrderCutQty || "{}");
    const reCutGarmentQtyMap = JSON.parse(orderFlow.orderConfig.reCutGarmentQty || "{}");
    const layList = Object.keys(layMap).flatMap(key => layMap[key]);
    const rowList = layList.flatMap(lay => lay.rowList.map(row => ({ ...row, marker: lay.marker, colorShade: lay.colorShade })));

    // Order Qty
    const orderQty = sizeList.reduce((acc, { dataKey }) => {
      const qty = order.fpoSizeBreakupList.filter(sb => sb.styleColor === styleColor && sb.size === dataKey).reduce((acc, sb) => acc + sb.qty, 0);
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalOrderQty = Object.keys(orderQty).reduce((acc, key) => acc + orderQty[key], 0);
    rows.push({ ...orderQty, styleColor, total: totalOrderQty, label: "Order Qty" });

    // Approved Qty
    const approvedQty = approvedOrderCutQtyMap[styleColor] || {};
    let totalApprovedQty = Object.keys(approvedQty).reduce((acc, key) => acc + approvedQty[key], 0);
    rows.push({ ...approvedQty, styleColor, total: totalApprovedQty, label: "Approved Qty" });

    // Cut Qty
    const cutQty = sizeList.reduce((acc, { dataKey }) => {
      const qty = rowList
        .filter(row => row.completed && row.styleColor === styleColor && row.colorShade !== LayType.RECUTTING)
        .reduce((acc, row) => {
          const sizeRatio = row.marker.sizeRatioList.find(sr => sr.size === dataKey);
          const plies = SpreadingType.FACE_ONE_WAY !== row.marker.spreadingType ? row.actualPly / 2 : row.actualPly;
          const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
          return acc + qty;
        }, 0);
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalCutQty = Object.keys(cutQty).reduce((acc, key) => acc + cutQty[key], 0);
    rows.push({ ...cutQty, styleColor, total: totalCutQty, label: "Cut Qty" });

    // Shortage/Excess
    const shortageExcessQty = sizeList.reduce((acc, { dataKey }) => {
      const qty = cutQty[dataKey] - approvedQty[dataKey];
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalShortageExcessQty = Object.keys(shortageExcessQty).reduce((acc, key) => acc + shortageExcessQty[key], 0);
    rows.push({ ...shortageExcessQty, styleColor, total: totalShortageExcessQty, label: "Shortage/Excess" });

    // Garment Recutting
    const reCutGarmentQty = reCutGarmentQtyMap[styleColor] || {};
    const reCutQty = sizeList.reduce((acc, { dataKey }) => {
      let qty = rowList
        .filter(row => row.completed && row.styleColor === styleColor && row.colorShade === LayType.RECUTTING)
        .reduce((acc, row) => {
          const sizeRatio = row.marker.sizeRatioList.find(sr => sr.size === dataKey);
          const plies = SpreadingType.FACE_ONE_WAY !== row.marker.spreadingType ? row.actualPly / 2 : row.actualPly;
          const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
          return acc + qty;
        }, 0);
      qty = qty + (reCutGarmentQty[dataKey] || 0);
      return { ...acc, [dataKey]: qty };
    }, {});
    let totalReCutQty = Object.keys(reCutQty).reduce((acc, key) => acc + reCutQty[key], 0);
    rows.push({ ...reCutQty, styleColor, total: totalReCutQty, label: "Garment Recutting" });
  } else if (dataType === DataType.GARMENT_RECUT_ROLL && order && orderFlow && fabrics) {
    const styleColorList = orderFlow.styleColorList || [];

    styleColorList.forEach(styleColor => {
      const flowInfo = orderFlow?.flowInfoList?.find(fi => fi.styleColor === styleColor);
      const part = flowInfo?.partList?.find(p => p.partName === orderFlow.part);
      const rollList = rollInfoList || [];

      let cu = part ? part.bomCu : 1.0;
      const markers = orderFlow.markerList && orderFlow.markerList.filter(m => m.completed && m.markerType === MarkerType.REMNANT && m.length > 0);
      if (markers && markers.length > 0) {
        cu = markers.sort((a, b) => a.layLength - b.layLength)[0].layLength;
      }

      if (rollList) {
        const groups = orderFlow.groupList && orderFlow.groupList.filter(g => g.type === GroupType.FINAL);
        rows = rows.concat(
          rollList
            .filter(
              roll =>
                roll.reCutGarment > 0 ||
                (roll.reserveType === ReserveType.ACROSS_GROUPS || roll.reserveType === ReserveType.SOFTWARE
                  ? parseFloat(roll.available) >= cu
                  : parseFloat(roll.available) + roll.reserved >= cu)
            )
            .map(roll => {
              const group = (groups && groups.find(g => g.id === roll.groupId)) || {};
              return {
                ...roll,
                rollNo: getRollNo(roll.rollNo),
                group,
                shrinkageValue: `${isEmpty(group?.patternNumber) ? "" : `${group.patternNumber} - `}${group.shrinkageValue}`,
                groupName: getGroupName(group),
                length: toFixed(roll.length),
                available: toFixed(roll.available),
                reCutGarment: toFixed(roll.reCutGarment)
              };
            })
        );
      }
    });
  } else if (dataType === DataType.GARMENT_RECUT_CUT_PLAN && order && order.fpoSizeBreakupList && orderFlow && layMap) {
    const markerList = orderFlow.markerList;
    const markers = markerList ? markerList.filter(m => m.markerType === MarkerType.RE_CUTTING) : [];
    const layList = layMap && layMap[LayType.RECUTTING] ? layMap[LayType.RECUTTING] : [];
    const colorList = getDistinctValues(order.fpoSizeBreakupList.map(sb => sb.color));
    const destList = getDistinctValues(order.fpoSizeBreakupList.filter(sb => sb.dest).map(sb => sb.dest));
    const sizeList = order.fpoSizeBreakupList
      .filter(sb => sb.color === colorList[0] && (destList.length === 0 || sb.dest === destList[0]))
      .sort((a, b) => a.serialNo - b.serialNo)
      .map(sb => sb.size);
    rows = markers
      .map(m => {
        const lay = (layList && layList.find(l => l.marker && l.marker.id === m.id)) || {};
        const ratios = m.sizeRatioList.reduce((acc, { size, ratio }) => ({ ...acc, [size]: ratio }), {});
        return {
          ...m,
          ...sizeList.reduce((acc, size) => ({ ...acc, [size]: ratios[size] || "-" }), {}),
          layLength: m.layLength || "-",
          groupName: `${toFixed(m.width, 1)}CM${m.warp !== null && m.weft !== null ? `, L${toFixedSigned(m.warp)}%W${toFixedSigned(m.weft)}` : ""}`,
          layNo: lay.layNo,
          status: lay.state,
          action: [
            <ImageEye key="view" style={{ cursor: "pointer", width: 24 }} onClick={onViewClick && onViewClick({ lay, layList, orderFlow })} />,
            m.layLength ? (
              <ContentCreate key="edit" style={{ cursor: "pointer", width: 24 }} onClick={onEditClick && onEditClick({ lay, layList, orderFlow })} />
            ) : null,
            lay.rowList?.length === 0 ? (
              <DeleteIcon
                key="delete"
                style={{ cursor: "pointer", width: 24 }}
                onClick={onDeleteClick && onDeleteClick({ lay, orderFlow, marker: m })}
              />
            ) : null,
            lay.syncCompleted === false ? (
              <SyncAlert key="sync-alert" onClick={onSyncClick && onSyncClick({ lay, orderFlow })} />
            ) : (
              <div key="empty" style={{ display: "inline-block", width: 24, height: 20 }} />
            )
          ]
        };
      })
      .sort((a, b) => a.layNo - b.layNo);
  }
  return rows;
};

export const fetchSetting = (key, dispatch, cb) => {
  const options = {
    url: `settings/${key}`,
    method: "get"
  };
  dispatch && dispatch({ type: FETCH_START });
  dataProvider(RestMethods.CUSTOM, undefined, options)
    .then(response => {
      if (response.status === 200) {
        cb && cb(response.data);
      }
      dispatch && dispatch({ type: FETCH_END });
    })
    .catch(err => {
      console.log(err);
      dispatch && dispatch({ type: FETCH_END });
    });
};

export const printLayslip = ({ order, orderFlow, layTab }) => {
  if (order && orderFlow && orderFlow.orderConfig) {
    var doc = new jsPDF();
    const heading = "Layslip for " + order.customer.name + " #" + order.poRef + ", " + layTab;
    setDefault(doc, heading);
    doc.setFontSize(10);

    const pageHeight = doc.internal.pageSize.getHeight();
    let startY = 10;

    let layList;
    if (layTab === "Remnant Lay") {
      if (orderFlow.orderConfig.reGeneratedLaySlip) {
        layList = JSON.parse(orderFlow.orderConfig.reGeneratedLaySlip);
      } else {
        const layMap = JSON.parse(orderFlow.orderConfig.laySlip);
        layList = layMap["Remnant Lay"];
      }
    } else {
      const layMap = JSON.parse(orderFlow.orderConfig.laySlip);
      layList = layMap[layTab];
    }
    if (!layList || layList.length === 0) return;

    let markers = new Set();
    layList.forEach((lay, idx) => {
      const marker = lay.marker;
      if (idx !== 0) {
        startY = doc.autoTable.previous.finalY;
      }
      if (!markers.has(lay.marker.id)) {
        if (pageHeight - startY < minReqHeight * 2) {
          doc.addPage();
          startY = 10;
        }
        const header = getHeader({ dataType: DataType.MARKER });
        const data = [{ ...marker, marker: marker.name, way: marker.way + " way", plies: marker.totalPlies }];
        doc.text("Marker #" + marker.markerNo, 7, startY + 7);
        doc.autoTable({
          startY: startY + 10,
          headStyles: { fillColor: [100, 175, 200], lineColor: 100 },
          columns: header,
          body: data
        });

        markers.add(marker.id);
      } else {
        if (pageHeight - startY < minReqHeight) {
          doc.addPage();
          startY = 10;
        }
      }
      startY = doc.autoTable.previous.finalY;
      const header = getHeader({ dataType: DataType.LAY });
      const data = getData({ dataType: DataType.LAY, lay });
      doc.text("Lay #" + lay.layNo, 7, startY + 7);
      doc.autoTable({ startY: startY + 10, columns: header, body: data });
    });

    doc.putTotalPages(totalPagesExp);
    doc.save("LAYSLIP " + order.customer.name + " " + order.poRef + " " + layTab + ".pdf");
  }
};

export const printBundleSheet = ({
  order,
  orderFlow,
  bundleSheet,
  type = BundleSheetType.NORMAL,
  tenant,
  flowInfoFields = [],
  orderFields = [],
  rollFields = [],
  i18nMessages
}) => {
  if (order && orderFlow && orderFlow.orderConfig && bundleSheet) {
    var doc = new jsPDF();
    const suffix = `${order.customer.name}, ${order.poRef}${order.noOfLots > 1 ? `, Lot-${orderFlow.lot}` : ""}`;
    const filename = `BUNDLE SHEET ${suffix}.pdf`;
    setDefault(doc, "BUNDLE SHEET REPORT", tenant);
    doc.setFontSize(10);
    const pageHeight = doc.internal.pageSize.getHeight();
    let startY = 24;

    const colorList = getDistinctValues(bundleSheet.map(b => b.color));

    colorList.forEach((color, idx) => {
      if (idx !== 0) {
        doc.addPage();
        startY = 10;
      }
      const bundles = bundleSheet
        .filter(bundle => bundle.color === color)
        .sort((a, b) => a.bundleNo - b.bundleNo)
        .map(b => ({ ...b, customer: order.customer.name, po: order.poRef }));

      ////// Print Order Info //////////////
      const orderInfo = layOrderInfoData(order, orderFlow);
      const fields = [...orderFields, ...flowInfoFields, "partName", "lot"];
      let columns = orderInfo.columns
        .filter(c => fields.includes(c.dataKey))
        .map(c => ({
          ...c,
          header: orderFields.includes(c.dataKey)
            ? lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header)
            : lodashGet(i18nMessages, `resources.orders.fields.flowInfo.${c.dataKey}`, c.header)
        }));

      const tableWidth = doc.internal.pageSize.getWidth() - 14;

      var autoTableOptions = {
        startY: startY + 10,
        body: orderInfo.body,
        columns
      };

      doc.text("Order Details", 7, startY + 7);
      addTable(doc, autoTableOptions, { tableWidth });

      let lays = [];
      bundles.forEach(bundle => {
        if (lays.length > 0) {
          if (lays[lays.length - 1] !== bundle.layNo) {
            lays.push(bundle.layNo);
          }
        } else {
          lays.push(bundle.layNo);
        }
      });

      const header = [
        { title: "Lay", dataKey: "layNo" },
        { title: "Size Group", dataKey: "sizeGroup" },
        { title: "Size", dataKey: "size" },
        { title: "Shade", dataKey: "shade" },
        { title: "Bundle", dataKey: "bundleNo" },
        { title: "Serial", dataKey: "stickerNo" },
        { title: type === BundleSheetType.NORMAL ? "Pieces" : "Pcs", dataKey: "pieces" },
        { title: type === BundleSheetType.NORMAL ? "Shrinkage" : "SHR.", dataKey: "shrinkage" },
        { title: "Roll", dataKey: "rollNo" },
        { title: "Supplier Roll", dataKey: "supplierRollNo" }
      ];

      startY = doc.autoTable.previous.finalY;
      doc.text(`Bundle Sheet`, 7, startY + 7);
      doc
        .setFont(undefined, "bold")
        .text(`Color : ${color}`, 45, startY + 7)
        .setFont(undefined, "normal");

      startY = startY + 3;

      lays.forEach(layNo => {
        let total = 0;
        let data = bundles
          .filter(bundle => bundle.layNo === layNo)
          .map(bundle => {
            let supplierRollNo = "";
            let rollNo = "";
            const rollList = Object.keys(JSON.parse(bundle.rolls || "{}"));
            const supplierRollList = Object.keys(JSON.parse(bundle.supplierRolls || "{}"));
            for (let i = 0; i < rollList.length; i++) {
              rollNo += getRollNo(rollList[i]) + ", ";
              if ((i + 1) % 8 === 0) {
                rollNo += "\n";
              }
            }
            rollNo = rollNo.substring(0, rollNo.lastIndexOf(","));
            for (let i = 0; i < supplierRollList.length; i++) {
              supplierRollNo += getRollNo(supplierRollList[i]) + ", ";
              if ((i + 1) % 8 === 0) {
                supplierRollNo += "\n";
              }
            }
            supplierRollNo = supplierRollNo.substring(0, supplierRollNo.lastIndexOf(","));

            const pieces = bundle.end - bundle.start + 1;
            total += pieces;
            return {
              ...bundle,
              sizeGroup: !isEmpty(bundle.sizeGroup) ? bundle.sizeGroup : `G1`,
              rollNo,
              supplierRollNo,
              stickerNo: `${bundle.start}-${bundle.end}`,
              pieces
            };
          });
        data.push({ stickerNo: "Total", pieces: total });

        if (pageHeight - (startY + 15) < minReqHeight) {
          doc.addPage();
          startY = 10;
        }
        if (type === BundleSheetType.NORMAL) {
          autoTableOptions = {
            startY: startY + 7,
            body: data,
            columns: header,
            styles: { halign: "center" },
            margin: { left: 7, right: 7 }
          };

          addTable(doc, autoTableOptions, { tableWidth, minCharLen: 7 });
          startY = doc.autoTable.previous.finalY;
        } else {
          const finalHeader = rollFields.includes("supplierRollNo")
            ? header.filter(h => h.dataKey !== "rollNo")
            : header.filter(h => h.dataKey !== "supplierRollNo");
          startY = print2TableColumnWise(doc, startY, finalHeader, data, tableWidth);
        }
      });
    });

    doc.putTotalPages(totalPagesExp);
    doc.save(filename);
  }
};

export const getPlannedLayList = cutOrder => {
  if (!cutOrder || !cutOrder.orderConfig || !cutOrder.orderConfig.layType) return [];
  let layMap = JSON.parse(cutOrder.orderConfig.laySlip);
  if (cutOrder.orderConfig.reGeneratedLaySlip) {
    const RemLay = JSON.parse(cutOrder.orderConfig.reGeneratedLaySlip);
    layMap["Remnant Lay"] = RemLay;
  }
  return Object.values(layMap)
    .flatMap(lMap => Object.values(lMap))
    .sort((a, b) => a.layNo - b.layNo);
};

const defTitleStyle = { fontSize: 7, fontFamily: "Courier", fontWeight: "bold" };
const defValueStyle = { fontSize: 7, fontFamily: "times", fontWeight: "normal" };
const getText = (value, charLength = 20) => (!isEmpty(value) ? multiLineText(`${value}`, charLength) : "");

export const printTickets = ({ order, orderFlow, bundleSheet, layNo, format = "A4", ticketSetting }) => {
  if (order && orderFlow && orderFlow.orderConfig && bundleSheet) {
    const suffix = `${order.customer.name}, ${`${order.poRef}`.replace(".", "")}${
      order.noOfLots > 1 ? `, Lot-${orderFlow.lot}` : ""
    }, Lay No-${layNo}`;
    const colorList = listFromCsv(orderFlow.colors);
    let bundleParts = orderFlow?.orderConfig?.bundleParts?.split(",") || [];

    let bundles = colorList.flatMap(color => {
      let list = bundleSheet
        .filter(bundle => bundle.color === color)
        .sort((a, b) => a.bundleNo - b.bundleNo)
        .map(b => ({
          ...b,
          customer: order.customer.name,
          poRef: order.poRef,
          lot: orderFlow.lot,
          noOfLots: order.noOfLots
        }));

      let layNos = [];
      list.forEach(bundle => {
        if (layNos.length > 0) {
          if (layNos[layNos.length - 1] !== bundle.layNo) {
            layNos.push(bundle.layNo);
          }
        } else {
          layNos.push(bundle.layNo);
        }
      });

      return layNos.flatMap(layNo => {
        let listLay = list.filter(bundle => bundle.layNo === layNo).sort((a, b) => a.bundle - b.bundleNo);
        return bundleParts.length > 0 ? bundleParts.flatMap(part => listLay.map(bundle => ({ ...bundle, bundlePart: part }))) : listLay;
      });
    });
    var doc;
    if (format === "A4") {
      const {
        copies = 1,
        options = { orientation: "p", unit: "mm", format: [210, 297] },
        columns,
        rows,
        fields = [],
        ticket = {}
      } = ticketSetting.A4 || {};

      const {
        defaultTitleStyle = defTitleStyle,
        defaultValueStyle = defValueStyle,
        defaultCharLength = 20,
        defaultValueType = "text",
        padding,
        margin
      } = ticket;

      doc = new jsPDF(options);

      const cellCount = columns * rows;
      const width = options.format[0]; // Page Width
      const height = options.format[1]; // Page Height
      const cellWidth = width / columns;
      const cellHeight = height / rows;

      bundles = bundles.flatMap(bundle =>
        Array(copies)
          .fill(0)
          .map(() => bundle)
      );

      let posX,
        posY,
        nextLayNo,
        docCellNumber = 0; // Cell Number across entire doc (not on one page)
      for (let i = 0; i < bundles.length; i++) {
        docCellNumber++;
        const bundle = bundles[i];
        let pageCellNumber = docCellNumber % cellCount; // Cell number on current page

        ///// Draw Vertical and horizontal line on each new page /////
        if (pageCellNumber === 1) {
          if (docCellNumber > 1) {
            doc.addPage();
          }
          doc.setDrawColor(150);
          // Draw Vertical Line //
          for (let i = 1; i < columns; i++) {
            const xFrom = (width * i) / columns;
            dottedLine(doc, xFrom, 0, xFrom, height, 2);
          }
          // Draw Horizontal Line //
          for (let i = 1; i < rows; i++) {
            const yFrom = (height * i) / rows;
            dottedLine(doc, 0, yFrom, width, yFrom, 2);
          }
        }

        let rowNo = (docCellNumber - 1) % cellCount;
        rowNo = Math.floor(rowNo / columns);

        posY = (height / rows) * rowNo;
        posX = (width / columns) * ((docCellNumber - 1) % columns);

        // On Lay Change, Print bundles in Fresh Row //
        nextLayNo = i + 1 < bundles.length ? bundles[i + 1].layNo : null;
        if (nextLayNo && `${nextLayNo}` !== `${bundle.layNo}` && docCellNumber % columns !== 0) {
          docCellNumber += columns - (docCellNumber % columns);
        }
        drawBundle(doc, fields, { ...bundle, size: `${bundle?.size}${bundle?.sizeGroup !== "G1" ? `(${bundle.sizeGroup})` : ""}` }, posX, posY, {
          width: cellWidth,
          height: cellHeight,
          padding,
          margin,
          defaultTitleStyle,
          defaultValueStyle,
          defaultCharLength,
          defaultValueType
        });
      }
    } else if (format.toUpperCase() === "TICKET") {
      const { ticket, fields = [], options = { orientation: "l", unit: "mm", format: [101, 76.2], compressPdf: true } } = ticketSetting.TICKET || {};
      const {
        defaultTitleStyle = defTitleStyle,
        defaultValueStyle = defValueStyle,
        defaultCharLength = 20,
        defaultValueType = "text",
        padding,
        margin
      } = ticket;

      const width = options.format[0];
      const height = options.format[1];

      doc = new jsPDF(options);

      doc.setFontSize(10);
      doc.setFont("courier", "bold");

      for (let i = 0; i < bundles.length; i++) {
        const bundle = bundles[i];
        const bundleNo = `${isTrue(bundle.isRecovery) ? "R" : ""}${bundle.bundleNo}`;
        // drawBundleTicket(doc, bundles[i]);*****
        drawBundle(doc, fields, { ...bundle, bundleNo, size: `${bundle?.size}${bundle?.sizeGroup !== "G1" ? `(${bundle.sizeGroup})` : ""}` }, 0, 0, {
          width,
          height,
          padding,
          margin,
          defaultTitleStyle,
          defaultValueStyle,
          defaultCharLength,
          defaultValueType
        });

        if (i < bundles.length - 1) {
          doc.addPage();
        }
      }
    }
    doc.save(`TICKETS ${suffix}.pdf`);
  }
};

export const printPieceTags = ({ order, orderFlow, bundleSheet, layNo, pieceTagSetting }) => {
  if (order && orderFlow && orderFlow.orderConfig && bundleSheet) {
    const {
      copies = 1,
      ticket,
      fields = [],
      options = { orientation: "l", unit: "mm", format: [101, 76.2], compressPdf: true }
    } = pieceTagSetting || {};

    const {
      defaultTitleStyle = defTitleStyle,
      defaultValueStyle = defValueStyle,
      defaultCharLength = 100,
      defaultValueType = "text",
      padding,
      margin
    } = ticket;
    const width = options.format[0];
    const height = options.format[1];

    let filename = `PIECE_TAGS`;
    var doc = new jsPDF(options);

    let pieceInfoList = [];

    for (let i = 0; i < bundleSheet?.length; i++) {
      const bundleInfo = bundleSheet[i];
      const bundleAdditionalInfo = isEmpty(bundleInfo?.additionalInfo) ? {} : JSON.parse(bundleInfo.additionalInfo);

      const serialStart = bundleInfo.start || 0;
      const serialEnd = bundleInfo.end || bundleInfo.qty;
      for (let j = serialStart; j <= serialEnd; j++) {
        const serialNo = `${j}`.padStart(4, "0");
        pieceInfoList.push({
          ...bundleInfo,
          ...bundleAdditionalInfo,
          serialNo: serialNo,
          code: `${bundleInfo.code},${serialNo}`,
          poRef: order?.poRef,
          buyer: order?.customer?.name,
          product: orderFlow?.product?.name,
          lot: `Lot ${orderFlow?.lot}`,
          bundleNo: bundleInfo?.bundleNo,
          layNo: bundleInfo?.layNo || bundleAdditionalInfo?.layNo,
          cutNo: bundleInfo?.cutNo || bundleAdditionalInfo?.cutNo
        });
      }
      pieceInfoList
        .sort((a, b) => a.serialNo - b.serialNo)
        .flatMap(ct =>
          Array(copies)
            .fill(0)
            .map(() => ct)
        );
    }

    for (let i = 0; i < pieceInfoList.length; i++) {
      const pieceInfo = pieceInfoList[i];
      drawPieceTag(doc, fields, pieceInfo, 0, 0, {
        width,
        height,
        padding,
        margin,
        defaultTitleStyle,
        defaultValueStyle,
        defaultCharLength,
        defaultValueType
      });

      if (i < pieceInfoList.length - 1) {
        doc.addPage();
      }
    }

    doc.save(filename.replaceAll(".", "_"));
  }
};

const drawPieceTag = (
  doc,
  fields,
  pieceInfo,
  pointX,
  pointY,
  { width, height, margin, defaultTitleStyle, defaultValueStyle, defaultCharLength, defaultValueType }
) => {
  doc.setDrawColor(0);
  //   const { left: ml = 2, right: mr = 2, top: mt = 2, bottom: mb = 2 } = margin || {};
  const { right: mr = 2, bottom: mb = 2 } = margin || {};
  //   doc.rect(pointX + ml, pointY + mt, width - ml - mr, height - mt - mb);

  //* Find Length of longest label per column **/
  //   const startXList = getDistinctValues(fields.filter(f => !isEmpty(f.coordinates)).map(field => field.coordinates[0] || 0));
  //   const labelLengthMap = startXList.reduce(
  //     (acc, startX) => ({
  //       ...acc,
  //       [startX]:
  //         fields
  //           .filter(f => isEmpty(f.valueType) || f.valueType === "text")
  //           .filter(f => !isEmpty(f.coordinates) && f.coordinates[0] === startX)
  //           .map(f => f.title || "")
  //           .sort((a, b) => b.length - a.length)[0]?.length || 7,
  //     }),
  //     {}
  //   );

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    const [startX, startY, endX, endY] = field.coordinates || [];
    const valueType = field.valueType || defaultValueType;

    // Write Label
    // const titleStyle = { ...defaultTitleStyle, ...field.titleStyle };
    // if (valueType !== "Barcode") {
    //   doc.setFontSize(titleStyle.fontSize);
    //   doc.setFont(titleStyle.fontFamily, titleStyle.fontWeight);
    //   doc.text(field.title || "", pointX + startX, pointY + startY);
    // }

    // const labelLength = labelLengthMap[startX] || 7;
    // const labelWidth = (field.title?.length || 7) * (titleStyle.fontSize / 4.5);
    // const labelWidth = labelLength * (titleStyle.fontSize / 4.5);
    const labelWidth = 0;

    // Write Value
    if (valueType === "text") {
      const valueStyle = { ...defaultValueStyle, ...field.valueStyle };
      const charLength = field.charLength || defaultCharLength;

      let value = getStrictText(pieceInfo[field.dataKey], charLength);
      if (!isEmpty(field.dataKeys)) {
        const dataKeys = getListFromCSV(field.dataKeys);
        const joinChar = field.joinChar || ",";
        value = dataKeys
          .map(dataKey => pieceInfo[dataKey])
          .filter(e => !isEmpty(e))
          .join(joinChar);
        value = getStrictText(value, charLength);
      }

      doc.setFontSize(valueStyle.fontSize);
      doc.setFont(valueStyle.fontFamily, valueStyle.fontWeight);
      doc.text(value, pointX + startX + labelWidth, pointY + startY, valueStyle.align || "left");
    }

    //** Print Barcode **//
    if (valueType === "Barcode") {
      const boxWidth = (endX || width - mr) - startX;
      const boxHeight = (endY || height - mb) - startY;
      //   const barCodeWidth = Math.min(boxWidth, boxHeight, field.width || 10);
      //   const barCodeHeight = Math.min(boxWidth, boxHeight, field.height || 10);

      const value = pieceInfo[field.dataKey];

      const canvas = document.createElement("canvas");
      JsBarcode(canvas, value, barcodeOptions);

      const imgData = canvas.toDataURL("image/png");

      doc.addImage(
        imgData,
        "PNG",
        // pointX + startX + (boxWidth - barCodeLength) / 2,
        // pointY + startY + (boxHeight - barCodeLength) / 2,
        pointX + startX,
        pointY + startY,
        boxWidth,
        boxHeight,
        "",
        "FAST"
      );
    }

    //** Print QR **//
    if (valueType === "QR") {
      const boxWidth = (endX || width - mr) - startX;
      const boxHeight = (endY || height - mb) - startY;
      let qrSize = Math.min(boxWidth, boxHeight);
      if (!isEmpty(field.size)) {
        qrSize = Math.min(qrSize, field.size);
      }

      const value = pieceInfo[field.dataKey];

      let svg = ReactDOMServer.renderToStaticMarkup(<QRCode value={`${value}`} level="H" />);
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const v = canvg.fromString(context, svg);
      v.render();
      const imgData = canvas.toDataURL("image/png");

      doc.addImage(imgData, "PNG", pointX + startX + (boxWidth - qrSize) / 2, pointY + startY + (boxHeight - qrSize) / 2, qrSize, qrSize, "", "FAST");
    }
  }
};

const drawBundle = (
  doc,
  fields,
  bundle,
  pointX,
  pointY,
  { width, height, margin, defaultTitleStyle, defaultValueStyle, defaultCharLength, defaultValueType }
) => {
  doc.setDrawColor(0);
  const { left: ml = 2, right: mr = 2, top: mt = 2, bottom: mb = 2 } = margin || {};
  doc.rect(pointX + ml, pointY + mt, width - ml - mr, height - mt - mb);

  //* Find Length of longest label per column **/
  const startXList = getDistinctValues(fields.filter(f => !isEmpty(f.coordinates)).map(field => field.coordinates[0] || 0));
  const labelLengthMap = startXList.reduce(
    (acc, startX) => ({
      ...acc,
      [startX]:
        fields
          .filter(f => isEmpty(f.valueType) || f.valueType === "text")
          .filter(f => !isEmpty(f.coordinates) && f.coordinates[0] === startX)
          .map(f => f.title || "")
          .sort((a, b) => b.length - a.length)[0]?.length || 7
    }),
    {}
  );

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    const [startX, startY, endX, endY] = field.coordinates || [];
    const valueType = field.valueType || defaultValueType;

    // Write Label
    const titleStyle = { ...defaultTitleStyle, ...field.titleStyle };
    if (valueType !== "QR") {
      doc.setFontSize(titleStyle.fontSize);
      doc.setFont(titleStyle.fontFamily, titleStyle.fontWeight);
      doc.text(field.title || "", pointX + startX, pointY + startY);
    }
    const labelLength = labelLengthMap[startX] || 7;
    // const labelWidth = (field.title?.length || 7) * (titleStyle.fontSize / 4.5);
    const labelWidth = labelLength * (titleStyle.fontSize / 4.5);

    // Write Value
    if (valueType === "text") {
      const valueStyle = { ...defaultValueStyle, ...field.valueStyle };
      const charLength = field.charLength || defaultCharLength;
      doc.setFontSize(valueStyle.fontSize);
      doc.setFont(valueStyle.fontFamily, valueStyle.fontWeight);
      doc.text(getText(bundle[field.dataKey], charLength), pointX + startX + labelWidth, pointY + startY);
    }

    if (valueType === "serial") {
      const labelWidth = titleStyle.fontSize * 1.5;
      // const boxWidth = (cellWidth - labelWidth - 2) / 2;

      const boxWidth = ((endX || width - mr) - startX - labelWidth) / 2 - 1;
      const lineHeight = endY - startY;

      // Draw boxes
      doc.rect(pointX + startX + labelWidth, pointY + startY - lineHeight / 2 - 0.5, boxWidth, lineHeight);
      doc.rect(pointX + startX + labelWidth + boxWidth + 1, pointY + startY - lineHeight / 2 - 0.5, boxWidth, lineHeight);

      // Write Serial
      doc.text(`${bundle.start || ""}`, pointX + startX + labelWidth + boxWidth / 2, pointY + startY, "center");
      doc.text(`${bundle.end || ""}`, pointX + startX + labelWidth + boxWidth + 1 + boxWidth / 2, pointY + startY, "center");
    }

    //** Print QR **//
    if (valueType === "QR") {
      const boxWidth = (endX || width - mr) - startX;
      const boxHeight = (endY || height - mb) - startY;
      const qrSize = Math.min(boxWidth, boxHeight, field.size || 10);

      let svg = ReactDOMServer.renderToStaticMarkup(<QRCode value={`${bundle.code || bundle.id}`} level="H" />);
      var canvas = document.createElement("canvas");
      var context = canvas.getContext("2d");
      const v = canvg.fromString(context, svg);
      v.render();
      var imgData = canvas.toDataURL("image/png");

      doc.addImage(imgData, "PNG", pointX + startX + (boxWidth - qrSize) / 2, pointY + startY + (boxHeight - qrSize) / 2, qrSize, qrSize, "", "FAST");
    }
  }
};

export const printFeedback = ({
  order,
  orderFlow,
  layList,
  layRowList,
  tenant,
  flowInfoFields = [],
  orderFields = [],
  rollFields = [],
  i18nMessages = {},
  unit = {}
}) => {
  if (order && orderFlow && orderFlow.orderConfig) {
    var doc = new jsPDF();

    setDefault(doc, "Feedback Report", tenant, null, unit);
    doc.setFontSize(11);

    const pageHeight = doc.internal.pageSize.getHeight();
    let startY = 30;

    ////// Print Order Info //////////////
    const orderInfo = layOrderInfoData(order, orderFlow);
    const fields = [...orderFields, ...flowInfoFields, "partName", "lot"];
    let columns = orderInfo.columns
      .filter(c => fields.includes(c.dataKey))
      .map(c => ({
        ...c,
        header: orderFields.includes(c.dataKey)
          ? lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header)
          : lodashGet(i18nMessages, `resources.orders.fields.flowInfo.${c.dataKey}`, c.header)
      }));

    const tableWidth = doc.internal.pageSize.getWidth() - 14;

    var autoTableOptions = {
      startY: startY + 3,
      body: orderInfo?.body,
      columns
    };

    doc.text("Order Details", 7, startY);
    addTable(doc, autoTableOptions, { tableWidth });

    let headerLay = [
      { dataKey: "serialNo", header: "Sl. No." },
      { dataKey: "rollNo", header: "F. Roll No" },
      { dataKey: "supplierRollNo", header: "S. Roll No" },
      { dataKey: "style", header: "Style" },
      { dataKey: "color", header: "Style Color" },
      { dataKey: "length", header: "Length" },
      { dataKey: "width", header: "Width" },
      { dataKey: "shade", header: "Shade" },
      { dataKey: "plannedPly", header: "Plan. \nPlies" },
      { dataKey: "plannedBalance", header: "Plan. \nBal." },
      { dataKey: "next", header: "Next" },
      { dataKey: "actualPly", header: "Actual \nPlies" },
      { dataKey: "actualBalance", header: "Actual \nBal." },
      { dataKey: "shortageExcess", header: "Short./ Excess" },
      { dataKey: "damaged", header: "Dam/ Rejected" },
      { dataKey: "widthVariation", header: "Width \nVar." },
      { dataKey: "reason", header: "Reason" }
    ];
    if (orderFlow?.styleList?.length <= 1) {
      headerLay = headerLay.filter(e => e.dataKey !== "style");
    }
    if (orderFlow?.colorList?.length <= 1) {
      headerLay = headerLay.filter(e => e.dataKey !== "color");
    }
    if (!rollFields.includes("supplierRollNo")) {
      headerLay = headerLay.filter(e => e.dataKey !== "supplierRollNo");
    }

    const finalLayList = layList
      .sort((a, b) => a.layNo - b.layNo)
      .map(lay => {
        const rowList = layRowList.filter(row => row.actualLayNo === lay.layNo && (row.completed || row.skipped));
        return { ...lay, rowList };
      });

    startY = doc.autoTable.previous.finalY + 3;
    finalLayList.forEach(lay => {
      const sizeGroup = getDistinctValues(lay.marker?.name?.split(", ").map(e => e.split("_")[0]));
      const markerName = lay.marker?.name
        ?.split(", ")
        .map(e => e.split("_")[1])
        .join(", ");

      const marker = `, Marker: ${markerName} (${sizeGroup} ), Lay Length = ${lay.marker.layLength} meters`;
      const externalLayNo = !isEmpty(lay?.extLayNo) ? ", external lay #" + lay?.extLayNo : "";
      doc.text("Lay #" + lay.layNo + (lay.layType === REMNANT_LAY ? "R" : "") + externalLayNo + marker, 7, startY + 7);

      let totalPlannedPly = 0,
        totalActualPly = 0,
        totalShortageExcess = 0;
      let dataLay = lay.rowList
        .sort((a, b) => (a.completed === b.completed ? a.serialNo - b.serialNo : a.completed ? -1 : 1))
        .map((row, idx) => {
          totalPlannedPly += row.plannedPly;
          totalActualPly += row.skipped ? 0 : row.actualPly;
          totalShortageExcess += Number(row.shortageExcess);

          const rollInfo = row.rollInfo;
          return {
            ...row,
            serialNo: idx + 1,
            plannedBalance: parseFloat(toFixed(row.plannedBalance, 2)),
            actualPly: row.skipped ? "" : row.actualPly,
            actualBalance: row.skipped ? "" : row.actualBalance,
            rollNo: rollInfo.rollNo,
            supplierRollNo: rollInfo.supplierRollNo,
            color: rollInfo.color,
            shade: rollInfo.shade,
            width: rollInfo.width,
            length: toFixed(row.available, 2) + " (" + toFixed(rollInfo.length, 2) + ")",
            layLength: lay.marker.layLength,
            shortageExcess: row.skipped ? "" : toFixed(row.shortageExcess, 1),
            widthVariation: isEmpty(row.widthVariation)
              ? ""
              : JSON.parse(row.widthVariation)
                  .map(e => `${e.width} cm: ${e.length} mtr`)
                  .join(",\n"),
            reason: isTrue(row.skipped) ? `Skipped\n(${row.reason})` : ""
          };
        });

      if (totalPlannedPly > 0) {
        dataLay.push({
          rollNo: "Total",
          plannedPly: totalPlannedPly,
          actualPly: totalActualPly,
          shortageExcess: parseFloat(toFixed(totalShortageExcess))
        });
      }
      if (dataLay.length > 0) {
        let finalHeaderLay = [...headerLay];
        if (marker?.markerType !== MarkerType.FINAL && marker?.markerType !== MarkerType.REMNANT) {
          finalHeaderLay = finalHeaderLay.filter(c => !["plannedPly", "plannedBalance", "next"].includes(c.dataKey));
        }

        autoTableOptions = {
          startY: startY + 10,
          body: dataLay,
          columns: finalHeaderLay,
          halign: "center"
        };

        var options = {
          tableWidth,
          minCharLen: 14,
          colWidthMap: { damaged: 15 }
        };

        addTable(doc, autoTableOptions, options);
        startY = doc.autoTable.previous.finalY + 4;
      } else {
        doc.text("Laying Not Started Yet.", 7, startY + 14);
        startY = startY + 18;
      }
      if (pageHeight - startY < minReqHeight) {
        doc.addPage();
        startY = 10;
      }
    });

    doc.putTotalPages(totalPagesExp);
    doc.save("FEEDBACK " + order.customer.name + " " + order.poRef + "-" + orderFlow.lot + ".pdf");
  }
};

export const printPartRecuttingReport = ({
  order,
  orderFlow,
  bundleParts,
  defectTypes,
  styleColor,
  reCutEndBitRollWise,
  rollList,
  tenant,
  flowInfoFields = [],
  orderFields = [],
  i18nMessages = {}
}) => {
  if (order && orderFlow && orderFlow.orderConfig) {
    var doc = new jsPDF();

    setDefault(doc, "Part Recutting Report", tenant);
    doc.setFontSize(10);

    let startY = 23;

    ////// Print Order Info //////////////

    const orderInfo = layOrderInfoData(order, orderFlow);
    const fields = [...orderFields, ...flowInfoFields, "partName", "lot"];
    let columns = orderInfo.columns
      .filter(c => fields.includes(c.dataKey))
      .map(c => ({
        ...c,
        header: orderFields.includes(c.dataKey)
          ? lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header)
          : lodashGet(i18nMessages, `resources.orders.fields.flowInfo.${c.dataKey}`, c.header)
      }));

    // orderInfo.header[5] = "Style Color";
    // orderInfo.data[0][5] = styleColor;

    doc.text("Order Details", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns, body: orderInfo.body });

    let header = getHeader({
      dataType: DataType.PART_RECUT_PART,
      order,
      bundleParts,
      defectTypes
    });
    let data = getData({
      dataType: DataType.PART_RECUT_PART,
      order,
      orderFlow,
      bundleParts,
      styleColor,
      defectTypes
    });

    startY = doc.autoTable.previous.finalY;
    doc.text("Part Recutting Details", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: header, body: data });

    header = getHeader({ dataType: DataType.ISSUED_ROLL, orderFlow });
    let issuedRolls = rollList.filter(roll => roll.usedInPart > 0).map((roll, idx) => ({ ...roll, serialNo: idx + 1 }));

    // End Bits
    if (!reCutEndBitRollWise) {
      const cu = getSmallestML(order, orderFlow, styleColor);
      const endBitRolls = rollList
        .filter(roll =>
          roll.reserveType === ReserveType.PER_ROLL_FIXED ? parseFloat(roll.available) + roll.reserved < cu : parseFloat(roll.available) < cu
        )
        .filter(roll => parseFloat(roll.usedInPart) === 0.0);
      const endBits = endBitRolls.reduce((acc, roll) => acc + parseFloat(roll.available), 0).toFixed(2);
      const reCutEndBitsMap = JSON.parse(orderFlow.orderConfig.reCuttingEndBits || "{}");
      const reCutEndBits = reCutEndBitsMap[styleColor] || 0;
      issuedRolls.unshift({ shade: "End Bits", length: endBits, usedInPart: reCutEndBits });
    }
    // Total
    let rowTotal = ["length", "usedInPart"].reduce(
      (acc, key) => ({ ...acc, [key]: issuedRolls.reduce((acc, roll) => acc + parseFloat(roll[key]), 0).toFixed(2) }),
      {}
    );
    issuedRolls.push({ ...rowTotal, shade: "Total" });

    startY = doc.autoTable.previous.finalY;
    doc.text("Issued Rolls", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: header, body: issuedRolls });

    doc.putTotalPages(totalPagesExp);
    doc.save("PART RECUTTING REPORT " + order.customer.name + " " + order.poRef + ".pdf");
  }
};

export const printGarmentRecuttingReport = ({
  order,
  orderFlow,
  styleColor,
  rowsCutSummary,
  rowsRoll,
  layMap,
  tenant,
  flowInfoFields = [],
  orderFields = [],
  i18nMessages = {}
}) => {
  if (order && orderFlow && orderFlow.orderConfig) {
    var doc = new jsPDF();
    setDefault(doc, "Garment Recutting Report", tenant);
    doc.setFontSize(10);

    let startY = 24;

    ////// Print Order Info //////////////
    const orderInfo = layOrderInfoData(order, orderFlow);
    const fields = [...orderFields, ...flowInfoFields, "partName", "lot"];
    let columns = orderInfo.columns
      .filter(c => fields.includes(c.dataKey))
      .map(c => ({
        ...c,
        header: orderFields.includes(c.dataKey)
          ? lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header)
          : lodashGet(i18nMessages, `resources.orders.fields.flowInfo.${c.dataKey}`, c.header)
      }));

    doc.text("Order Details", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns, body: orderInfo.body });

    let header = getHeader({ dataType: DataType.GARMENT_RECUT_SUMMARY, order, orderFlow });
    startY = doc.autoTable.previous.finalY;
    doc.text("Cut Summary", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: header, body: rowsCutSummary });

    header = getHeader({ dataType: DataType.GARMENT_RECUT_CUT_PLAN });
    let data = getData({
      dataType: DataType.GARMENT_RECUT_CUT_PLAN,
      order,
      orderFlow,
      styleColor,
      layMap
    });
    header = header.slice(0, header.length - 3);
    if (data.length > 0) {
      startY = doc.autoTable.previous.finalY;
      doc.text("Manual Markers", 7, startY + 7);
      doc.autoTable({ startY: startY + 10, columns: header, body: data });
    }

    header = getHeader({ dataType: DataType.GARMENT_RECUT_ROLL });
    header.pop();
    let usedRolls = rowsRoll.filter(roll => roll.reCutGarment > 0 && roll.shade !== "Total").map((roll, idx) => ({ ...roll, serialNo: idx + 1 }));

    let rowTotal = ["length", "reCutGarment"].reduce(
      (acc, key) => ({ ...acc, [key]: usedRolls.reduce((acc, roll) => acc + parseFloat(roll[key]), 0).toFixed(2) }),
      {}
    );
    usedRolls.push({ ...rowTotal, shade: "Total" });

    startY = doc.autoTable.previous.finalY;
    doc.text("Used Rolls", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: header, body: usedRolls });

    doc.putTotalPages(totalPagesExp);
    doc.save("GARMENT RECUTTING REPORT " + order.customer.name + " " + order.poRef + ".pdf");
  }
};

// const drawBundleA4 = (doc, startX, startY, bundle = {}) => {
//   doc.setDrawColor(0);
//
//   doc.rect(startX, startY, 60, 50);
//   doc.setFontSize(7);
//   doc.setFont("Courier", "Bold");
//
//   doc.text("CUSTOMER :", startX + 2, startY + 6);
//   doc.text("LAY NO:", startX + 2, startY + 14);
//   doc.text("SIZE  :", startX + 2, startY + 22);
//   doc.text("BUNDLE:", startX + 2, startY + 30);
//   doc.text("SHADE :", startX + 2, startY + 38);
//   doc.text("QTY:", startX + 2, startY + 46);
//
//   doc.text("PO    :", startX + 24, startY + 6);
//   doc.text("PART  :", startX + 24, startY + 14);
//   doc.text("STYLE :", startX + 24, startY + 22);
//   doc.text("COLOR :", startX + 24, startY + 30);
//   doc.text("SHR:", startX + 24, startY + 38);
//   doc.text("SERIAL:", startX + 17, startY + 46);
//
//   doc.rect(startX + 30, startY + 42, 12, 6);
//   doc.rect(startX + 45, startY + 42, 12, 6);
//
//   doc.setFont("times", "normal");
//   doc.text(multiLineText(bundle.customer, 9), startX + 13, startY + 6);
//   doc.text(`${bundle.layNo || ""}`, startX + 13, startY + 14);
//   doc.text(multiLineText(bundle.size, 9), startX + 13, startY + 22);
//   doc.text(`${bundle.shade || ""}`, startX + 13, startY + 38);
//
//   doc.text(multiLineText(bundle.part, 20), startX + 35, startY + 14);
//   doc.text(multiLineText(bundle.style, 20), startX + 35, startY + 22);
//   doc.text(multiLineText(bundle.color, 20), startX + 35, startY + 30);
//   doc.text(`${bundle.shrinkage || ""}`, startX + 35, startY + 38);
//
//   const poWithLot = `${bundle.po}${bundle.noOfLots > 1 ? ` (Lot-${bundle.lot})` : ""}`;
//   doc.setFontSize(9);
//   doc.setFont("Courier", "Bold");
//   doc.text(multiLineText(poWithLot, 20), startX + 35, startY + 6);
//   doc.text(`${bundle.bundleNo || ""}`, startX + 15, startY + 30);
//   doc.text(`${bundle.end - bundle.start + 1 || ""}`, startX + 10, startY + 46);
//   doc.text(`${bundle.start || ""}`, startX + 36, startY + 46, "center");
//   doc.text(`${bundle.end || ""}`, startX + 51, startY + 46, "center");
// };
//
// const drawBundleTicket = (doc, bundle) => {
//   doc.setDrawColor(0);
//   const startX = 11.5;
//   const startY = 4;
//   doc.rect(10, 4, 80, 44);
//
//   // Column 1
//   doc.text("LAY NO:", startX + 2, startY + 6);
//   doc.text("PART  :", startX + 2, startY + 14);
//   doc.text("SIZE  :", startX + 2, startY + 22);
//   doc.text("BUNDLE:", startX + 2, startY + 30);
//   doc.text("QTY   :", startX + 2, startY + 38);
//
//   // Column 2
//   doc.text("CUSTOMER :", startX + 30, startY + 6);
//   doc.text("PO    :", startX + 30, startY + 14);
//   doc.text("STYLE :", startX + 30, startY + 22);
//   doc.text("COLOR :", startX + 30, startY + 30);
//   doc.text("SERIAL:", startX + 30, startY + 38);
//
//   doc.rect(startX + 45, startY + 34, 12, 6);
//   doc.rect(startX + 60, startY + 34, 12, 6);
//
//   doc.text(`${bundle.layNo || ""}`, startX + 17, startY + 6);
//   doc.text(multiLineText(bundle.part, 20), startX + 17, startY + 14);
//   doc.text(multiLineText(bundle.size, 9), startX + 17, startY + 22);
//
//   doc.text(multiLineText(bundle.customer, 9), startX + 45, startY + 6, 0.5);
//   doc.text(multiLineText(bundle.style, 20), startX + 45, startY + 22);
//   doc.text(multiLineText(`${bundle.color} (${bundle.shade})`, 20), startX + 45, startY + 30);
//
//   let po = bundle.po;
//   if (bundle.lots > 1) {
//     po = `${bundle.po} (Lot${bundle.lot})`;
//   }
//
//   doc.text(multiLineText(po, 15), startX + 45, startY + 14);
//   doc.text(`${bundle.bundleNo || ""}`, startX + 17, startY + 30);
//   doc.text(`${bundle.end - bundle.start + 1 || ""}`, startX + 17, startY + 38);
//   doc.text(`${bundle.start || ""}`, startX + 51, startY + 38, "center");
//   doc.text(`${bundle.end || ""}`, startX + 66, startY + 38, "center");
// };

const getStrictText = (value, charLength = 20) => {
  if (isEmpty(value)) return "";
  value = `${value}`;
  const valueLength = value.length;
  const lines = Math.ceil(valueLength / charLength);
  return Array(lines)
    .fill("")
    .map((_, idx) => value.substring(idx * charLength, (idx + 1) * charLength));
};

const dottedLine = (doc, xFrom, yFrom, xTo, yTo, segmentLength) => {
  // Calculate line length (c)
  var a = Math.abs(xTo - xFrom);
  var b = Math.abs(yTo - yFrom);
  var c = Math.sqrt(Math.pow(a, 2) + Math.pow(b, 2));

  // Make sure we have an odd number of line segments (drawn or blank)
  // to fit it nicely
  var fractions = c / segmentLength;
  var adjustedSegmentLength = Math.floor(fractions) % 2 === 0 ? c / Math.ceil(fractions) : c / Math.floor(fractions);

  // Calculate x, y deltas per segment
  var deltaX = adjustedSegmentLength * (a / c);
  var deltaY = adjustedSegmentLength * (b / c);

  var curX = xFrom,
    curY = yFrom;
  while (curX <= xTo && curY <= yTo) {
    doc.line(curX, curY, curX + deltaX, curY + deltaY);
    curX += 2 * deltaX;
    curY += 2 * deltaY;
  }
};

const multiLineText = (text, lineWidth) => {
  let result = [];
  if (text) {
    if (text.length > lineWidth) {
      let idx = text.substring(0, lineWidth).lastIndexOf(" ");
      result.push(text.substring(0, idx));
      result.push(text.substring(idx + 1));
    } else {
      result.push(text);
    }
  }
  return result;
};

// const orderInfoData = (order, orderFlow) => {
//   let orderInfo = {};
//   if (order) {
//     let { styleName, customer, customerPoRef, poRef, season, totalQty, extra } = order;
//     const header = ["Customer", "Customer PO Ref", "PO Ref", "Part", "Lot", "Colors", "Season", "Style", "Order Qty", "Extra(%)"];
//     const data = [
//       [
//         customer.name,
//         customerPoRef || "",
//         poRef,
//         orderFlow.part,
//         "Lot-" + orderFlow.lot,
//         orderFlow.colors,
//         season.name,
//         styleName,
//         totalQty,
//         extra + "%"
//       ]
//     ];
//     orderInfo = { header, data };
//   }
//   return orderInfo;
// };

const print2TableColumnWise = (doc, startY, header, data, tableWidth) => {
  let YAxis;
  if (data.length > 3) {
    let mid;
    mid = data.length % 2 === 0 ? parseInt(data.length / 2) : parseInt(data.length / 2) + 1;

    var autoTableOptions = {
      startY: startY + 7,
      body: data.slice(0, mid),
      columns: header,
      margin: { left: 7, right: 107 }
    };

    var options = {
      tableWidth: tableWidth / 2,
      minCharLen: 15,
      maxCharLen: 30
    };

    addTable(doc, autoTableOptions, options);
    YAxis = doc.autoTable.previous.finalY;

    // Reset page to the same as before previous table
    let newPage = doc.internal.getCurrentPageInfo().pageNumber;

    doc.setPage(1 + doc.internal.getCurrentPageInfo().pageNumber - doc.autoTable.previous.pageCount);

    autoTableOptions = {
      startY: startY + 7,
      body: data.slice(mid, data.length),
      columns: header,
      margin: { left: 107, right: 7 }
    };

    addTable(doc, autoTableOptions, options);
    if (doc.internal.getCurrentPageInfo().pageNumber > newPage) {
      newPage = doc.internal.getCurrentPageInfo().pageNumber;
    }
    doc.setPage(newPage);
  } else {
    autoTableOptions = {
      startY: startY + 7,
      body: data.slice(0, data.length),
      columns: header,
      margin: { left: 7, right: 107 }
    };

    addTable(doc, autoTableOptions, options);
    YAxis = doc.autoTable.previous.finalY;
  }
  return YAxis;
};

// const flowColumns = [
//   { dataKey: "customerFlowRef", header: "Customer Flow Ref" },
//   { dataKey: "flowRef", header: "Flow Ref" },
//   { dataKey: "productName", header: "Product" },
//   { dataKey: "style", header: "Style" },
//   { dataKey: "color", header: "Color" },
//   { dataKey: "partName", header: "Part" },
//   { dataKey: "lot", header: "Lot" },
//   { dataKey: "orderQty", header: "Order Qty" },
//   { dataKey: "extra", header: "Extra Cut (%)" },
//   { dataKey: "inseam", header: "Inseam" },
//   { dataKey: "destination", header: "Destination" },
//   { dataKey: "exFactoryDate", header: "Delivery Date" }
// ];

export const printLayFeedback = ({
  order,
  orderFlow,
  lay,
  tenant,
  layFields,
  flowInfoFields = [],
  orderFields = [],
  rollFields = [],
  i18nMessages = {},
  unit = {}
}) => {
  var doc = new jsPDF();
  // Header
  setDefault(doc, "Lay Feedback Report", tenant, null, unit);

  doc.setFontSize(11);
  let startY = 30;

  ////// Print Order Info //////////////
  const orderInfo = layOrderInfoData(order, orderFlow);

  const fields = [...orderFields, ...flowInfoFields, "partName", "lot"];
  let columns = orderInfo.columns
    .filter(c => fields.includes(c.dataKey))
    .map(c => ({
      ...c,
      header: orderFields.includes(c.dataKey)
        ? lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header)
        : lodashGet(i18nMessages, `resources.orders.fields.flowInfo.${c.dataKey}`, c.header)
    }));

  const tableWidth = doc.internal.pageSize.getWidth() - 14;

  var autoTableOptions = {
    startY: startY + 3,
    body: orderInfo.body,
    columns
  };

  doc.text("Order Details", 7, startY);
  addTable(doc, autoTableOptions, { tableWidth });

  //// cut summary ////
  startY = doc.autoTable.previous.finalY;

  const cutSummaryInfo = getCutSummaryInfo(lay, orderFlow);

  autoTableOptions = {
    startY: startY + 10,
    body: cutSummaryInfo?.body,
    columns: cutSummaryInfo?.columns,
    halign: "center"
  };

  doc.text("Cut Summary", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth });

  ////// Print Marker Details //////////////
  const markerInfo = getMarkerInfo(lay, orderFlow, tenant);
  columns = markerInfo.columns.map(c => ({ ...c, header: lodashGet(i18nMessages, `resources.rollPlan.fields.layslip.${c.dataKey}`, c.header) }));
  startY = doc.autoTable.previous.finalY;

  autoTableOptions = {
    startY: startY + 10,
    body: markerInfo?.body,
    columns,
    halign: "center"
  };

  doc.text("Marker Info", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth });

  ////// Print Spreading Details //////////////
  const spreadingInfo = getSpreadingInfo(lay, orderFlow, layFields);

  startY = doc.autoTable.previous.finalY;

  autoTableOptions = {
    startY: startY + 10,
    body: spreadingInfo?.body,
    columns: spreadingInfo?.columns,
    halign: "center"
  };

  doc.text("Spreading Info", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth });

  ////// Print Lay Feedback  //////////////
  const layData = getLayFeedbackData(lay, orderFlow, rollFields, tenant);
  columns = layData.columns || [];

  startY = doc.autoTable.previous.finalY;

  autoTableOptions = {
    startY: startY + 10,
    body: layData?.body,
    columns,
    halign: "center"
  };

  var options = {
    tableWidth,
    colWidthMap: { damaged: 15, consignment: 13 }
  };

  doc.text("Layslip", 7, startY + 7);
  addTable(doc, autoTableOptions, options);

  startY = doc.autoTable.previous.finalY;

  doc.text("QC Name and Signature", 7, startY + 20, "left");
  doc.text("Cutting Incharge Signature", doc.internal.pageSize.getWidth() - 7, startY + 20, "right");

  //TODO: Rename File Name of the PDF.
  const filename = `LAY SLIP - ${order.customer.name} ${order.poRef}.pdf`;

  doc.putTotalPages(totalPagesExp);
  doc.save(filename);
};

const getLayFeedbackData = (lay, cutOrder, rollFields = [], tenant = {}) => {
  if (isEmpty(lay)) return { columns: [], body: [] };

  const marker = cutOrder.markerList.find(m => m.id === lay.marker?.id) || {};

  let laySlipInfo = { columns: [], body: [] };
  let columnList = [
    { dataKey: "serialNo", header: "Sl. No." },
    { dataKey: "rollNo", header: "F. Roll No" },
    { dataKey: "supplierRollNo", header: "S. Roll No" },
    { dataKey: "style", header: "Style" },
    { dataKey: "color", header: "Style Color" },
    { dataKey: "length", header: "Length" },
    { dataKey: "width", header: "Width" },
    { dataKey: "consignment", header: "Consignment" },
    { dataKey: "shade", header: "Shade" },
    { dataKey: "plannedPly", header: "Plan. \nPlies" },
    { dataKey: "plannedBalance", header: "Plan. \nBal." },
    { dataKey: "next", header: "Next" },
    { dataKey: "actualPly", header: "Actual Plies" },
    { dataKey: "actualBalance", header: "Actual \nBal." },
    { dataKey: "shortageExcess", header: "Short./ Excess" },
    { dataKey: "damaged", header: "Dam/ Rejected" },
    { dataKey: "widthVariation", header: "Width \nVar." },
    { dataKey: "reason", header: "Reason" }
  ];
  if (cutOrder?.styleList?.length <= 1) {
    columnList = columnList.filter(e => e.dataKey !== "style");
  }
  if (cutOrder?.colorList?.length <= 1) {
    columnList = columnList.filter(e => e.dataKey !== "color");
  }
  if (rollFields.includes("supplierRollNo")) {
    columnList = columnList.filter(e => e.dataKey !== "supplierRollNo");
  }

  if (marker?.markerType !== MarkerType.FINAL && marker?.markerType !== MarkerType.REMNANT) {
    columnList = columnList.filter(e => !["plannedPly", "plannedBalance", "next"].includes(e.dataKey));
  }

  if (tenant?.tenantId !== "pacific") {
    columnList = columnList.filter(e => e.dataKey !== "consignment");
  }

  const rows = lay.rowList
    .filter(row => row.completed || row.skipped)
    .sort((a, b) => (a.completed === b.completed ? a.serialNo - b.serialNo : a.completed ? -1 : 1))
    .map((row, idx) => {
      const rollInfo = row.rollInfo || {};
      return {
        ...row,
        serialNo: idx + 1,
        plannedBalance: parseFloat(toFixed(row.plannedBalance, 2)),
        actualPly: row.skipped ? "" : row.actualPly,
        actualBalance: row.skipped ? "" : row.actualBalance,
        supplierRollNo: rollInfo.supplierRollNo,
        rollNo: rollInfo.rollNo,
        color: rollInfo.color,
        shade: rollInfo.shade,
        width: rollInfo.width,
        consignment: rollInfo.consignment,
        length: toFixed(row.available, 2) + " (" + toFixed(rollInfo.length, 2) + ")",
        layLength: lay.marker.layLength,
        shortageExcess: row.skipped ? "" : toFixed(row.shortageExcess, 1),
        widthVariation: isEmpty(row.widthVariation)
          ? ""
          : JSON.parse(row.widthVariation)
              .map(e => `${e.width} cm: ${e.length} mtr`)
              .join(",\n"),
        reason: isTrue(row.skipped) ? `Skipped\n(${row.reason})` : ""
      };
    });

  const keys = ["currLength", "length", "plannedPly", "actualPly", "shortageExcess"];
  const totalRow = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: toFixed(
        rows.filter(row => !isEmpty(row[key])).reduce((acc, row) => acc + parseFloat(row[key]), 0),
        0
      )
    }),
    {}
  );
  rows.push({
    ...totalRow,
    rollLength: `${toFixed(totalRow.currLength, 1)} (${toFixed(totalRow.length, 1)})`,
    rollNo: "Total"
  });

  laySlipInfo = { columns: columnList, body: rows };
  return laySlipInfo;
};

const getSpreadingInfo = (lay, orderFlow, layFields = []) => {
  if (isEmpty(lay)) return { columns: [], body: [] };
  let columnList = [
    { dataKey: "layNo", header: "Lay No" },
    { dataKey: "extLayNo", header: "ERP Lay No" },
    { dataKey: "tableNo", header: "Table No" },
    { dataKey: "cutNo", header: "Cut No" },
    { dataKey: "prodType", header: "Production Type" },
    { dataKey: "cutPlanner", header: "Cut Planner" },
    { dataKey: "spreaderName", header: "Spreader Name" },
    { dataKey: "startTime", header: "Start Time" },
    { dataKey: "endTime", header: "End Time" }
  ];

  columnList = columnList.filter(c => layFields.includes(c.dataKey));

  const body = [
    {
      layNo: lay.layNo,
      extLayNo: lay.extLayNo,
      date: !isEmpty(lay.startAt) ? format(lay.startAt, "dd MMM yy") : "",
      tableNo: lay.table?.name || "",
      spreaderName: lay.spreader?.fullName || "",
      cutNo: lay.cutNo || "",
      prodType: lay.prodType || "",
      cutPlanner: orderFlow?.plannedBy,
      startTime: !isEmpty(lay.startAt) ? format(lay.startAt, "dd MMM yy, hh:mm a") : "",
      endTime: !isEmpty(lay.endAt) ? format(lay.endAt, "dd MMM yy, hh:mm a") : ""
    }
  ];

  return { columns: columnList, body: body };
};

const getCutSummaryInfo = (lay, orderFlow) => {
  const marker = orderFlow?.markerList?.find(m => m.id === lay.marker?.id) || {};
  const sizeRatioList = marker?.sizeRatioList || [];
  const sizeList = sizeRatioList.sort((a, b) => a.serialNo - b.serialNo).map(sr => ({ dataKey: sr.sizeKey, header: sr.size }));

  let columns = [
    { dataKey: "style", header: "Style" },
    { dataKey: "color", header: "Color" },
    { dataKey: "info", header: "Info" },
    ...sizeList,
    { dataKey: "total", header: "Total" }
  ];

  if (orderFlow?.colorList.length <= 1) {
    columns = columns.filter(e => e.dataKey !== "color");
  }

  if (orderFlow?.styleList.length <= 1) {
    columns = columns.filter(e => e.dataKey !== "style");
  }

  const body = orderFlow?.styleColorList?.flatMap(sc => {
    const [style, color] = sc.split(DEFAULT_SEPARATOR);
    const plannedPlies = lay.rowList?.filter(e => e.styleColor === sc).reduce((acc, row) => acc + row.plannedPly, 0);
    const actualPlies = lay.rowList
      ?.filter(e => e.styleColor === sc)
      .filter(row => row.completed && !row.skipped)
      .reduce((acc, row) => acc + row.actualPly, 0);

    let rows = [
      {
        style,
        color,
        info: "Planned Qty",
        total: marker?.noOfWays * plannedPlies,
        ...sizeRatioList.reduce((acc, sr) => ({ ...acc, [sr.sizeKey]: sr.ratio * plannedPlies }), {})
      },
      {
        style,
        color,
        info: "Cut Qty",
        total: marker?.noOfWays * actualPlies,
        ...sizeRatioList.reduce((acc, sr) => ({ ...acc, [sr.sizeKey]: sr.ratio * actualPlies }), {})
      }
    ];
    if (orderFlow?.orderConfig?.setting?.multiPartBundling === MULTI_PART_MANUAL_PLAN) {
      rows = rows.slice(1);
    }

    return rows;
  });

  return { body, columns };
};

const getMarkerInfo = (lay, orderFlow, tenant) => {
  if (isEmpty(lay) || isEmpty(orderFlow?.markerList)) return { columns: [], body: [] };

  const marker = orderFlow.markerList.find(m => m.id === lay.marker?.id) || {};

  const sizeRatioList = marker?.sizeRatioList || [];

  let columnList = [
    { dataKey: "markerNo", header: "Marker No" },
    { dataKey: "fpoMarkerNo", header: "Ext. Marker No" },
    { dataKey: "sizeGroup", header: "Size Group" },
    { dataKey: "markerName", header: "Marker" },
    { dataKey: "way", header: "Way" },
    { dataKey: "markerLength", header: "Marker Length" },
    { dataKey: "layLength", header: "Lay Length" },
    { dataKey: "width", header: "Width" },
    { dataKey: "pattern", header: "Pattern" },
    { dataKey: "csv", header: "CSV" }
  ];
  if (isEmpty(tenant?.tenantId) || !["pacific", "pacific_test"].includes(tenant.tenantId)) {
    columnList = columnList.filter(c => c.dataKey !== "fpoMarkerNo");
  }

  const sizeGroup = getDistinctValues(marker.name?.split(", ").map(e => e.split("_")[0]));

  const markerName = marker?.name
    ?.split(", ")
    .map(e => e.split("_")[1])
    .join(", ");

  let pattern = "";
  if (!isEmpty(marker.group?.warpPattern) && !isEmpty(marker.group?.weftPattern)) {
    pattern = `${marker.group?.patternNumber || ""} (L${toFixedSigned(marker.group.warpPattern, 1)}% W${toFixedSigned(
      marker.group.weftPattern,
      1
    )}%)`;
  } else if (!isEmpty(marker?.warpPattern) && !isEmpty(marker?.weftPattern)) {
    pattern = `${marker.patternNumber || ""} (L${toFixedSigned(marker.warpPattern, 1)}% W${toFixedSigned(marker.weftPattern, 1)}%)`;
  }

  const body = [
    {
      sizeGroup,
      fpoMarkerNo: `M${marker?.fpoMarkerNo || ""}`,
      markerNo: `M${marker?.markerNo || ""}`,
      markerName,
      way: marker.noOfWays,
      markerLength: `${toFixedValue(marker.length)} m`,
      layLength: `${toFixedValue(marker.layLength)} m`,
      width: marker?.markerType === MarkerType.MANUAL ? `${marker?.width} cm` : `${marker.group?.widthAxis} cm`,
      pattern,
      csv: marker.group?.csv || marker?.csv,
      ...sizeRatioList.reduce((acc, sr) => ({ ...acc, [sr.sizeKey]: sr.ratio }), {})
    }
  ];
  return { columns: columnList, body: body };
};

// const layFlowDetails = (order, orderFlow) => {
//   let orderInfo = { columns: [], body: [] };
//   let columnList = flowColumns;

//   const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);

//   const body = flowInfoList?.map(flowInfo => {
//     return {
//       customerFlowRef: flowInfo?.customerFlowRef,
//       flowRef: flowInfo?.flowRef,
//       productName: flowInfo?.product?.name,
//       style: flowInfo?.style,
//       color: flowInfo?.color,
//       lot: `Lot ${orderFlow.lot}`,
//       partName: orderFlow.part,
//       orderQty: flowInfo?.orderQty,
//       extra: !isEmpty(flowInfo?.extra) ? flowInfo?.extra : "",
//       inseam: !isEmpty(flowInfo?.inseam) ? flowInfo?.inseam : "",
//       destination: !isEmpty(flowInfo?.destination) ? flowInfo?.destination : "",
//       exFactoryDate: !isEmpty(flowInfo?.exFactoryDate) ? format(flowInfo?.exFactoryDate, "dd MMM yy") : ""
//     };
//   });

//   orderInfo = { columns: columnList, body: body };
//   return orderInfo;
// };

const layOrderInfoData = (order, orderFlow) => {
  const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);

  let columnList = [
    { dataKey: "poRef", header: "PO Ref" },
    { dataKey: "customerName", header: "Customer" },
    { dataKey: "productName", header: "Product" },
    { dataKey: "style", header: "Style Name" },
    { dataKey: "styleNo", header: "Style No" },
    { dataKey: "color", header: "Style Color" },
    { dataKey: "partName", header: "Part" },
    { dataKey: "lot", header: "Lot" },
    { dataKey: "inseam", header: "Inseam" },
    { dataKey: "destination", header: "Destination" },
    { dataKey: "deliveryDate", header: "Del. Date" },
    { dataKey: "flowRef", header: "Line ID" },
    { dataKey: "customerFlowRef", header: "BPO" }
  ];

  //** Exclude Fields if value is empty in each flowInfo **//
  if (flowInfoList.every(fi => isEmpty(fi.styleNo))) {
    columnList = columnList.filter(c => c.dataKey !== "styleNo");
  }
  if (flowInfoList.every(fi => isEmpty(fi.fit))) {
    columnList = columnList.filter(c => c.dataKey !== "fit");
  }
  if (flowInfoList.every(fi => isEmpty(fi.inseam))) {
    columnList = columnList.filter(c => c.dataKey !== "inseam");
  }
  if (flowInfoList.every(fi => isEmpty(fi.destination))) {
    columnList = columnList.filter(c => c.dataKey !== "destination");
  }
  if (flowInfoList.every(fi => isEmpty(fi.deliveryDate))) {
    columnList = columnList.filter(c => c.dataKey !== "deliveryDate");
  }
  if (flowInfoList.every(fi => isEmpty(fi.flowRef))) {
    columnList = columnList.filter(c => c.dataKey !== "flowRef");
  }
  if (flowInfoList.every(fi => isEmpty(fi.customerFlowRef))) {
    columnList = columnList.filter(c => c.dataKey !== "customerFlowRef");
  }

  const body = flowInfoList.map(fi => ({
    poRef: order.poRef,
    customerName: order.customer?.name,
    seasonName: order.season?.name,
    productName: fi.product?.name,
    style: fi.style,
    styleNo: fi.styleNo,
    color: fi.color,
    partName: orderFlow.part,
    lot: orderFlow.lotValue,
    inseam: fi.inseam,
    destination: fi.destination,
    deliveryDate: fi.deliveryDate,
    flowRef: fi.flowRef,
    customerFlowRef: fi.customerFlowRef,
    orderQty: fi.orderQty,
    extra: fi.extra
  }));

  return { columns: columnList, body };
};
